import { TFunction } from "react-i18next";
import { mapToBase } from "go-core/api/searchSelectService";
import { MoneyApi } from "go-core/api/types";
import { convertBoolean } from "go-core/components/Formatters/FormatBoolean";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { adminApi } from "../../../../../services/Api/Admin/adminApi";
import { OrganizationApi } from "../../../../../services/Api/types";

class ReportOrganizationUtils {
	static getSelectedColumns = (type: string) => {
		const columnsFromLocalStorage = window.localStorage.getItem(`go_report.${type}.columns`)?.split(",");
		if (columnsFromLocalStorage) {
			return columnsFromLocalStorage;
		}
		return ["count"];
	};
	static getGroupsInfo = (t: TFunction) => {
		return {
			STATUS: (groupByValue: Record<string, any>) => {
				if (groupByValue.name === "ENABLED")
					return t(`enums.common.status.${groupByValue.name}`, { ns: "lib" });
				return t(`enums.common.status.${groupByValue.name}`);
			},
			CATEGORY: (groupByValue: Record<string, any>) => {
				return t(`enums.organization_category.${groupByValue.name}`, { ns: "admin" });
			},
			RULES_ACCEPTED: (groupByValue: Record<string, any>) => {
				return convertBoolean(groupByValue.name === "true", t);
			},
			LICENSE_TYPE: (groupByValue: Record<string, any>) => {
				if (groupByValue.name === "IN_TRIAL") return t("enums.licenses.TRIAL", { ns: "admin" });
				return t(`enums.licenses.${groupByValue.name}`, { ns: "admin" });
			},
			BILLING_PERIOD: (groupByValue: Record<string, any>) => {
				return t(`enums.provisions.period.${groupByValue.name}`, { ns: "admin" });
			},
		};
	};

	static getFilters = (t: TFunction) => {
		return [
			{
				id: "partner",
				name: t("common.word.partner", { ns: "admin" }),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminPartnersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				options: {
					ENABLED: "Aktywny",
					BLOCKED: "Zablokowany",
					REMOVED: "Usunięty",
				},
			},
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
			},
			{
				id: "category",
				name: `${t("common.word.category", { ns: "admin" })}`,
				type: "list",
				options: {
					CLUB: `${t("enums.organization_category.CLUB", { ns: "admin" })}`,
					RESTAURANT: `${t("enums.organization_category.RESTAURANT", { ns: "admin" })}`,
					CAFE: `${t("enums.organization_category.CAFE", { ns: "admin" })}`,
					FOODTRUCK: `${t("enums.organization_category.FOODTRUCK", { ns: "admin" })}`,
					PUB_BAR: `${t("enums.organization_category.PUB_BAR", { ns: "admin" })}`,
					SUSHI: `${t("enums.organization_category.SUSHI", { ns: "admin" })}`,
					SHOP: `${t("enums.organization_category.SHOP", { ns: "admin" })}`,
					OTHERS: `${t("enums.organization_category.OTHERS", { ns: "admin" })}`,
				},
			},
			{
				id: "license_expires_at_year",
				name: t("modules.organization.field.license_expires_at_year.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "license_expires_at_month",
				name: t("modules.organization.field.license_expires_at_month.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "license_expires_at_day",
				name: t("modules.organization.field.license_expires_at_day.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (data: OrganizationApi) => FormatDate(data.created_at)?.split(",")[0],
			},
			{
				id: "activated_at",
				name: t("common.word.activated_at", { ns: "admin" }),
				type: "date",
				render: (data: OrganizationApi) => FormatDate(data.activated_at)?.split(",")[0],
			},
			{
				id: "last_used_at",
				name: t("common.word.last_used_at", { ns: "admin" }),
				type: "date",
				render: (data: OrganizationApi) => FormatDate(data.last_used_at)?.split(",")[0],
			},
			{
				id: "application_type",
				name: `${t("common.word.applications", { ns: "admin" })}`,
				type: "list",
				options: {
					GOPOS: "GoPOS",
				},
			},
			{
				id: "license_type",
				name: t("common.word.license_type", { ns: "admin" }),
				type: "list",
				options: {
					STANDARD: t("enums.licenses.STANDARD", { ns: "admin" }),
					FUTURE: t("enums.licenses.FUTURE", { ns: "admin" }),
					CANCELLED: t("enums.licenses.CANCELLED", { ns: "admin" }),
					TRIAL: t("enums.licenses.TRIAL", { ns: "admin" }),
					PAUSED: t("enums.licenses.PAUSED", { ns: "admin" }),
					MANUAL: t("enums.licenses.MANUAL", { ns: "admin" }),
					NONE: t("enums.licenses.NONE", { ns: "admin" }),
				},
			},
			{
				id: "license_expires_at",
				name: t("modules.organization.field.license_expires_at.title", { ns: "admin" }),
				type: "date",
			},
			{
				id: "gopos_last_used_at",
				name: t("modules.organization.field.gopos_last_used_at.title", { ns: "admin" }),
				type: "date",
			},
			{
				id: "gopos_created_at",
				name: t("modules.organization.field.gopos_created_at.title", { ns: "admin" }),
				type: "date",
			},
			{
				id: "gopos_license_expires_at",
				name: t("modules.organization.field.gopos_license_expires_at.title", { ns: "admin" }),
				type: "date",
			},
			{
				id: "gopos_activated_at",
				name: t("modules.organization.field.gopos_activated_at.title", { ns: "admin" }),
				type: "date",
			},
			{
				id: "company_tax_id_no",
				name: t("lib:common.word.tax_id_no.title"),
				type: "text",
			},
			{
				id: "company_id",
				name: t("common.word.company_id", { ns: "admin" }),
				type: "text",
			},
			{
				id: "company_street",
				name: t("modules.organization.field.company_street.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "company_city",
				name: t("modules.organization.field.company_city.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "company_email",
				name: t("modules.organization.field.company_email.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "company_name",
				name: t("common.word.company_name", { ns: "admin" }),
				type: "text",
			},
			{
				id: "company_phone_number",
				name: t("modules.organization.field.company_phone_number.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "place_name",
				name: t("modules.organization.field.place_name.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "place_street",
				name: t("lib:common.address.street"),
				type: "text",
			},
			{
				id: "place_city",
				name: t("lib:common.address.city"),
				type: "text",
			},
			{
				id: "place_email",
				name: t("lib:common.word.email"),
				type: "text",
			},
			{
				id: "place_phone_number",
				name: t("lib:common.word.phone_number"),
				type: "text",
			},
			{
				id: "place_website",
				name: t("modules.organization.field.place_website.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "place_facebook",
				name: t("modules.organization.field.place_facebook.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "place_instagram",
				name: t("modules.organization.field.place_instagram.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "place_linkedin",
				name: t("modules.organization.field.place_linkedin.title", { ns: "admin" }),
				type: "text",
			},
			{
				id: "terminal_count",
				name: t("modules.organization.field.terminal_count.title", { ns: "admin" }),
				type: "number",
			},
			{
				id: "tag",
				name: `${t("common.word.tags", { ns: "admin" })}`,
				type: "search_select",
				source: {
					request: async (search: string) => {
						const f = mapToBase(search, { resource_type: "ORGANIZATION" });
						return adminApi.getTagsListSearchSelect(f);
					},
				},
			},
			{
				id: "mrr",
				name: `${t("common.word.mrr", { ns: "admin" })}`,
				type: "number",
			},
			{
				id: "mrr_sum",
				name: `${t("common.word.mrr_sum", { ns: "admin" })}`,
				type: "number",
			},
			{
				id: "arpu",
				name: `${t("modules.organization.field.arpu.title", { ns: "admin" })}`,
				type: "number",
			},
			{
				id: "cancellation_reason",
				name: `${t("common.word.cancellation_reason", { ns: "admin" })}`,
				type: "text",
			},
			{
				id: "migrated_at",
				name: `${t("common.word.migrated_at", { ns: "admin" })}`,
				type: "date",
			},
			{
				id: "customer_lifetime",
				name: `${t("modules.organization.field.customer_lifetime.title", { ns: "admin" })}`,
				type: "number",
			},
			{
				id: "partner_type",
				name: `${t("common.word.partner_type", { ns: "admin" })}`,
				type: "list",
				options: {
					RESELLER: "Reseller",
					KAM: "KAM",
					TOP: "TOP",
					SALE: "Sale",
					CS: "CS",
					CSV: "CSv",
					SALE_PREMIUM: "Sale premium",
					AFFILIATE: "AFFILIATE",
				},
			},
			{
				id: "billing_period",
				name: `${t("common.word.license_type", { ns: "admin" })}`,
				type: "list",
				options: {
					MONTHLY: t("enums.provisions.period.MONTHLY", { ns: "admin" }),
					YEARLY: t("enums.provisions.period.YEARLY", { ns: "admin" }),
				},
			},
			{
				id: "first_paid_at",
				name: t("common.word.first_paid_at", { ns: "admin" }),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "feature_potential_count",
				name: "Feature - ilość potencjalna",
				type: "number",
			},
		];
	};

	static getColumns = (t: TFunction) => {
		return [
			{
				id: "count",
				name: t("common.word.amount", { ns: "admin" }),
			},
			{
				id: "mrr",
				name: t("common.word.mrr", { ns: "admin" }),
				render: (item: MoneyApi) => FormatMoney(item),
			},
			{
				id: "mrr_sum",
				name: t("common.word.mrr_sum", { ns: "admin" }),
				render: (item: MoneyApi) => FormatMoney(item),
			},
		];
	};

	static getGroups = (t: TFunction) => {
		return [
			{
				id: "CATEGORY",
				name: t("common.word.category", { ns: "admin" }),
			},
			{
				id: "APPLICATION_TYPE",
				name: t("modules.organization.field.application_type.title", { ns: "admin" }),
			},
			{
				id: "STATUS",
				name: t("lib:common.word.status"),
			},
			{
				id: "PARTNER",
				name: t("common.word.partner", { ns: "admin" }),
			},
			{
				id: "RULES_ACCEPTED",
				name: t("modules.organization.field.rules_accepted.title", { ns: "admin" }),
			},
			{
				id: "COMPANY_NAME",
				name: t("common.word.company_name", { ns: "admin" }),
			},
			{
				id: "COMPANY_ID",
				name: "Company ID",
			},
			{
				id: "COMPANY_CITY",
				name: t("modules.organization.field.company_city.title", { ns: "admin" }),
			},
			{
				id: "PLACE_NAME",
				name: t("modules.organization.field.place_name.title", { ns: "admin" }),
			},
			{
				id: "PLACE_STREET",
				name: t("lib:common.address.street"),
			},
			{
				id: "PLACE_CITY",
				name: t("lib:common.address.city"),
			},
			{
				id: "PLACE_EMAIL",
				name: t("lib:common.word.email"),
			},
			{
				id: "PLACE_PHONE_NUMBER",
				name: t("lib:common.word.phone_number"),
			},
			{
				id: "PLACE_WEBSITE",
				name: t("modules.organization.field.place_website.title", { ns: "admin" }),
			},
			{
				id: "PLACE_FACEBOOK",
				name: t("modules.organization.field.place_facebook.title", { ns: "admin" }),
			},
			{
				id: "PLACE_INSTAGRAM",
				name: t("modules.organization.field.place_instagram.title", { ns: "admin" }),
			},
			{
				id: "PLACE_LINKEDIN",
				name: t("modules.organization.field.place_linkedin.title", { ns: "admin" }),
			},
			{
				id: "TERMINAL_COUNT",
				name: t("modules.organization.field.terminal_count.title", { ns: "admin" }),
			},
			{
				id: "LICENSE_TYPE",
				name: t("common.word.license_type", { ns: "admin" }),
			},
			{
				id: "PARTNER_KAM",
				name: t("modules.organization.field.partner_kam.title", { ns: "admin" }),
			},
			{
				id: "PARTNER_CS",
				name: t("modules.organization.field.partner_cs.title", { ns: "admin" }),
			},
			{
				id: "PARTNER_TOP",
				name: t("modules.organization.field.partner_top.title", { ns: "admin" }),
			},
			{
				id: "PARTNER_SALE",
				name: t("modules.organization.field.partner_sale.title", { ns: "admin" }),
			},
			{
				id: "PARTNER_RESELLER",
				name: t("modules.organization.field.partner_reseller.title", { ns: "admin" }),
			},
			{
				id: "PARTNER_AFFILIATE",
				name: t("modules.organization.field.partner_affiliate.title", { ns: "admin" }),
			},
			{
				id: "CANCELLATION_REASON",
				name: t("common.word.cancellation_reason_of_subscription", { ns: "admin" }),
			},
			{
				id: "BILLING_PERIOD",
				name: t("common.word.license_type", { ns: "admin" }),
			},
			{
				id: "NAME",
				name: t("lib:common.word.name"),
			},
			{
				id: "PARTNER_TYPE",
				name: t("common.word.partner_type", { ns: "admin" }),
			},
			{
				id: "FIRST_PAID_AT",
				name: t("common.word.first_paid_at", { ns: "admin" }),
			},
			{
				id: "FIRST_PAID_AT_DAY",
				name: t("modules.organization.field.first_paid_at_day.title", { ns: "admin" }),
			},
			{
				id: "FIRST_PAID_AT_MONTH",
				name: t("modules.organization.field.first_paid_at_month.title", { ns: "admin" }),
			},
			{
				id: "FIRST_PAID_AT_YEAR",
				name: t("modules.organization.field.first_paid_at_year.title", { ns: "admin" }),
			},
			{
				id: "CREATED_AT",
				name: t("lib:common.word.created_at"),
			},
			{
				id: "CREATED_AT_DAY",
				name: t("lib:common.word.created_at"),
			},
			{
				id: "CREATED_AT_MONTH",
				name: t("modules.organization.field.created_at_month.title", { ns: "admin" }),
			},
			{
				id: "CREATED_AT_YEAR",
				name: t("modules.organization.field.created_at_year.title", { ns: "admin" }),
			},
			{
				id: "LICENSE_EXPIRES_AT_YEAR",
				name: t("modules.organization.field.license_expires_at_year.title", { ns: "admin" }),
			},
			{
				id: "LICENSE_EXPIRES_AT_MONTH",
				name: t("modules.organization.field.license_expires_at_month.title", { ns: "admin" }),
			},
			{
				id: "LICENSE_EXPIRES_AT_DAY",
				name: t("modules.organization.field.license_expires_at_day.title", { ns: "admin" }),
			},
			{
				id: "LICENSE_EXPIRES_AT",
				name: t("modules.organization.field.license_expires_at.title", { ns: "admin" }),
			},
		];
	};
}

export default ReportOrganizationUtils;
